import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PrimaryButton = styled.button`
  display: block;
  background-color: #b573ff;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 9999px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  width: auto;
  min-width: 240px;
  max-width: 100%;
  margin: 0;
  font-size: 1rem;
  height: 60px;
  
  &:hover {
    background-color: #9b5ed9;
  }
`;

const DenominationButton = styled.button`
  background-color: #1a1a1a;
  color: white;
  padding: 1rem;
  border-radius: 4px;
  border: ${props => props.selected ? '1px solid white' : '1px solid #333'};
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    border-color: white;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const QuantityButton = styled.button`
  background-color: #1a1a1a;
  color: white;
  width: 50px;
  height: 50px;
  border: 1px solid #333;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  
  &:hover {
    border-color: white;
  }
`;

const QuantityInput = styled.div`
  width: 100px;
  height: 50px;
  border: 1px solid white;
  border-radius: 4px;
  margin: 0 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const SectionTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  font-weight: normal;
  opacity: 0.8;
`;

const GiftCardWrapper = styled.div`
  background-color: #000;
  color: white;
  min-height: 100vh;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #4CAF50;
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  opacity: ${props => props.visible ? '1' : '0'};
  transform: translateY(${props => props.visible ? '0' : '20px'});
  transition: opacity 0.3s, transform 0.3s;
`;

const GiftCardPage = () => {
  const navigate = useNavigate();
  const [denomination, setDenomination] = useState(40);
  const [quantity, setQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);

  const denominationOptions = [20, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200, 500];

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  const handleAddToCart = () => {
    // Create gift card object
    const giftCard = {
      id: 'gc-' + Date.now(), // Simple unique ID
      type: 'gift_card',
      name: `Digital Gift Card (€${denomination})`,
      price: denomination,
      quantity: quantity,
      imageUrl: '/images/gift-card-w-bg.svg'
    };

    // Get existing cart from localStorage or initialize empty array
    let cart = JSON.parse(localStorage.getItem('cart') || '[]');

    // Add new item to cart
    cart.push(giftCard);

    // Save updated cart to localStorage
    localStorage.setItem('cart', JSON.stringify(cart));

    // Dispatch custom event to notify components of cart change
    window.dispatchEvent(new Event('cartUpdated'));

    // Show toast notification
    setShowToast(true);

    // Hide toast after 2 seconds
    setTimeout(() => {
      setShowToast(false);
      // Redirect to cart page
      navigate('/cart');
    }, 1000);
  };

  return (
    <GiftCardWrapper className="overflow-x-hidden">
      <div>
        <div className="flex flex-col items-center justify-start w-full !bg-cover !bg-center pt-[14vw]" style={{
          backgroundImage: "url('/images/header-small.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}>
          <h1 className="max-w-[80%] md:max-w-none text-[30px] md:text-[55px] leading-tight font-light text-white text-center px-8 md:px-16">Give the Gift of a Memorable Moment</h1>
          <p className="px-8 md:px-16 text-accent text-center max-w-[920px] mx-auto text-[16px] md:text-[20px] mb-4">with Our Gift Card</p>
        </div>

        <div className="px-8 md:px-16 w-full grid !grid-cols-1 md:!grid-cols-2 gap-16 items-center relative mt-[30px] md:mt-[90px] mb-[300px]">
          <img src="/images/gradients/gradient-5.svg" className="absolute right-[-210px] top-[-200px] md:top-[-300px] md:left-[-460px] md:!w-[916px] h-auto" />
          <img src="/images/gradients/gradient-6.svg" className="absolute left-[-210px] top-[200px] md:top-[240px] md:left-[120px] md:!w-[916px] h-auto opacity-50" />

          <div className="relative mb-10 md:mb-0 md:pr-20">
            <img src="/images/gift-card-w-bg.svg" alt="About Map" className="w-[863px] -mb-[20px] h-auto" />
          </div>

          <div className="w-full relative z-10">
            <h2 className="text-[31px] font-light">Digital Gift Card</h2>
            <p className="text-[16px] md:text-[20px] mb-6">Finding the perfect gift can be challenging. Give the gift of choice with a digital gift card! After checkout, the gift card code will be sent via email.</p>
            <p className="text-[16px] md:text-[20px] mb-14">Let the recipient choose the special moment they want to capture in the form of a starry sky!</p>

            <SectionTitle>Card Denomination</SectionTitle>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-10">
              {denominationOptions.slice(0, 6).map((value) => (
                <DenominationButton
                  key={value}
                  selected={denomination === value}
                  onClick={() => setDenomination(value)}
                >
                  € {value}.00
                </DenominationButton>
              ))}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-14">
              {denominationOptions.slice(6).map((value) => (
                <DenominationButton
                  key={value}
                  selected={denomination === value}
                  onClick={() => setDenomination(value)}
                >
                  € {value}.00
                </DenominationButton>
              ))}
            </div>

            <SectionTitle>Quantity</SectionTitle>
            <QuantityControl>
              <QuantityButton onClick={decreaseQuantity}>-</QuantityButton>
              <QuantityInput>{quantity}</QuantityInput>
              <QuantityButton onClick={increaseQuantity}>+</QuantityButton>
            </QuantityControl>

            <PrimaryButton onClick={handleAddToCart}>
              Add to Cart
            </PrimaryButton>
          </div>
        </div>

      </div>

      <Toast visible={showToast}>
        Added to cart!
      </Toast>
    </GiftCardWrapper>
  );
};

export default GiftCardPage; 